import type { DealLead, LeadSortKey, SortDir } from "./types";

/** Tier letter → marker/badge color (shared A–E "Deal Tier v3" scorer). */
export const TIER_COLORS: Record<string, string> = {
  A: "#16a34a",
  B: "#65a30d",
  C: "#f59e0b",
  D: "#f97316",
  E: "#dc2626",
};
export const UNRANKED_COLOR = "#6b7280";
export const PROBATE_COLOR = "#8b5cf6";

/** Extract the tier letter from a "Deal Tier v3" value like "⛔ E — Pass". */
export const tierLetter = (tier: string | null | undefined): string => {
  const m = String(tier ?? "").match(/[A-E]/);
  return m ? m[0] : "";
};

export const tierColor = (tier: string | null | undefined): string =>
  TIER_COLORS[tierLetter(tier)] ?? UNRANKED_COLOR;

/** "$123,457" — or "—" when null. */
export const fmtBid = (n: number | null | undefined): string =>
  n != null ? "$" + Math.round(n).toLocaleString() : "—";

/** First 5 digits, as a string — tolerates numeric zips and ZIP+4. */
export const zip5 = (z: unknown): string =>
  String(z ?? "")
    .replace(/\D/g, "")
    .slice(0, 5);

/** Stable identity for a lead across auctions + probates ("auction:rec123"). */
export const leadKey = (item: Pick<DealLead, "kind" | "id">): string =>
  `${item.kind}:${item.id}`;

/** The auction "value" column: minimum bid, falling back to opening bid. */
export const leadValue = (item: DealLead): number | null =>
  item.kind === "auction"
    ? (item.minimumBid ?? item.openingBid)
    : item.estValue;

export const leadOwner = (item: DealLead): string | null =>
  item.kind === "auction" ? item.owner : item.decedent;

export const leadDate = (item: DealLead): string | null =>
  item.kind === "auction" ? item.saleDate : item.filingDate;

export const hasCoords = (
  item: Pick<DealLead, "lat" | "lng">,
): item is { lat: number; lng: number } & DealLead =>
  item.lat != null && item.lng != null && item.lat !== 0 && item.lng !== 0;

export interface ViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Viewport filter for the table: leads with coordinates must sit inside the
 * current map bounds; leads without coordinates (un-geocoded probates) always
 * pass — they're nowhere on the map, so the viewport says nothing about them.
 * Handles the antimeridian (west > east) for completeness.
 */
export const inViewport = (
  item: Pick<DealLead, "lat" | "lng">,
  bounds: ViewportBounds | null,
): boolean => {
  if (!bounds || !hasCoords(item)) return true;
  if (item.lat < bounds.south || item.lat > bounds.north) return false;
  return bounds.west <= bounds.east
    ? item.lng >= bounds.west && item.lng <= bounds.east
    : item.lng >= bounds.west || item.lng <= bounds.east;
};

/**
 * YYYY-MM-DD cutoff for the "hide auctions whose sale date passed more than
 * N months ago" setting. Returns null when the setting is off (0 / undefined).
 */
export const pastAuctionCutoff = (
  months: number | undefined,
  now: Date = new Date(),
): string | null => {
  if (!months) return null;
  const d = new Date(now);
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
};

const sortValue = (item: DealLead, key: LeadSortKey): string | number => {
  switch (key) {
    case "type":
      return item.kind;
    case "address":
      return String(item.address ?? "").toLowerCase();
    case "owner":
      return String(leadOwner(item) ?? "").toLowerCase();
    case "date":
      return String(leadDate(item) ?? "");
    case "value": {
      const v = leadValue(item);
      return v == null ? -Infinity : v;
    }
    case "tier": {
      // A best → sorts first ascending; unranked always last.
      const l = tierLetter(item.dealTier);
      return l ? l.charCodeAt(0) : 999;
    }
  }
};

/** Immutable sort of the visible lead list by a table column. */
export const sortLeads = (
  list: DealLead[],
  key: LeadSortKey | null,
  dir: SortDir,
): DealLead[] => {
  if (!key) return list;
  const sign = dir === "desc" ? -1 : 1;
  return [...list].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (va < vb) return -sign;
    if (va > vb) return sign;
    return 0;
  });
};

/** Skip-trace already queued / running / done? (worker-side status string) */
export const isTraced = (item: DealLead): boolean =>
  (item.kind === "probate" && !!item.runSkipTrace) ||
  /queue|pending|process|in.?progress|complete|done/i.test(
    item.skipTraceStatus ?? "",
  );
